import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getAllClass, getLevelById } from "../../api/pedagogicElement";
import { Select } from "antd";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { useEffect, useState } from "react";
import { getAllAcademicYear } from "../../api/generalParam";
import { getStudentRegistrationByClass } from "../../api/etudiants";
import { getMemoryByID, updateMemory } from "../../api/memory";

type Props = {
  show: boolean;
  idMemory: string;
  onClose: () => void;
  onSaved?: () => void;
};

interface Year {
  id: string;
  label: string;
  status: string;
}

const EditMemoryModal = ({ show, idMemory, onClose, onSaved }: Props) => {
  const getModalContainer = () => {
    const modalElement = document.getElementById("edit_memoire_modal");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();
  const [allClasses, setAllClasses] = useState<any>([]);
  const [years, setYears] = useState<Year[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<any>(null);
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [allStudents, setAllStudents] = useState<any>([]);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [memoryData, setMemoryData] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const createSchema = z.object({
    id_academicYear: z
      .string()
      .nonempty("Veuillez choisir une année académique"),
    id_class: z.string().nonempty("Veuillez choisir une classe"),
    id_studentProfil: z.string().nonempty("Veuillez choisir un étudiant"),
    title: z.string().nonempty("Veuillez saisir le thème du mémoire"),
    director_memory: z
      .string()
      .nonempty("Veuillez saisir l'encadrant du mémoire"),
    member_of_jury1: z
      .string()
      .nonempty("Veuillez saisir le jury de soutenance"),
    member_of_jury2: z
      .string()
      .nonempty("Veuillez saisir le jury de soutenance"),
    defense_date: z.string().nonempty("Veuillez saisir la date de soutenance"),
    place_of_defense: z
      .string()
      .nonempty("Veuillez saisir le lieu de soutenance"),
    note: z.string().nonempty("Veuillez saisir la note de soutenance"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de modification d'un mémoire
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await updateMemory(idMemory, data);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Mémoire modifié !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchYears = async () => {
    try {
      const response = await getAllAcademicYear();
      const formattedYears = response.data;
      setYears(formattedYears);

      setHasLoaded(true);
      setIsLoading(false);
    } catch (error: any) {
      // console.log("erreur de chargement des années académiques : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les années académiques en cas d'erreur
      setYears([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  // ✅ Refactored: pass preselection data as parameters

  const fetchClasses = async (
    academicYearId: string,
    preselectedClassId?: string,
    preselectedStudentId?: string,
    autoSelectFirst?: boolean,
  ) => {
    try {
      const response = await getAllClass(
        academicYearId,
        buildPermissionContext(Resource.MEMOIRE, Action.UPDATE),
      );
      const classes = response.data;

      // 🔄 Enrichir chaque classe avec son niveau
      const formattedClasses = await Promise.all(
        classes.map(async (cls: any) => {
          try {
            const levelResponse = await getLevelById(
              cls.Level?.id,
              buildPermissionContext(Resource.MEMOIRE, Action.UPDATE),
            );
            return {
              ...cls,
              level: levelResponse.data,
            };
          } catch (error: any) {
            // console.error("Erreur récupération niveau :", cls.id_level);
            toast.error(
              error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                "Une erreur s'est produit, Réessayer plus tard",
            );
            return {
              ...cls,
              level: null,
            };
          }
        }),
      );

      // 🔠 TRI : niveau puis classe
      const sortedClasses = formattedClasses.sort((a: any, b: any) => {
        const levelA = a.level?.label || "";
        const levelB = b.level?.label || "";
        const levelCompare = levelA.localeCompare(levelB, "fr", {
          sensitivity: "base",
        });
        if (levelCompare !== 0) return levelCompare;
        const classA = a.label || "";
        const classB = b.label || "";
        return classA.localeCompare(classB, "fr", { sensitivity: "base" });
      });

      setAllClasses(sortedClasses);

      if (preselectedClassId) {
        // Mode édition : présélection par ID
        const selectedClassFromData = sortedClasses.find(
          (c: any) => c.id === preselectedClassId,
        );
        if (selectedClassFromData) {
          setSelectedClass(selectedClassFromData);
          setValue("id_class", selectedClassFromData.id);
          await fetchStudents(selectedClassFromData.id, preselectedStudentId);
        }
      } else if (autoSelectFirst && sortedClasses.length > 0) {
        // Mode changement d'année : sélectionner la première classe automatiquement
        const firstClass = sortedClasses[0];
        setSelectedClass(firstClass);
        setValue("id_class", firstClass.id);
        // Charger et sélectionner le premier étudiant de cette classe
        await fetchStudents(firstClass.id, undefined, true);
      }
    } catch (error: any) {
      // console.log("erreur de chargement des classes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      setAllClasses([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const fetchStudents = async (
    classId: string,
    preselectedStudentId?: string,
    autoSelectFirst?: boolean,
  ) => {
    try {
      const response = await getStudentRegistrationByClass(
        classId,
        buildPermissionContext(Resource.MEMOIRE, Action.UPDATE),
      );
      const students = response.data || [];
      setAllStudents(students);

      if (preselectedStudentId) {
        // Mode édition : présélection par ID
        const found = students.find(
          (s: any) => s.StudentProfil.id === preselectedStudentId,
        );
        if (found) {
          setSelectedStudent(found);
          setValue("id_studentProfil", found.StudentProfil.id);
        }
      } else if (autoSelectFirst && students.length > 0) {
        // ✅ Mode changement de classe : sélectionner le premier étudiant
        const first = students[0];
        setSelectedStudent(first);
        setValue("id_studentProfil", first.StudentProfil.id);
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Une erreur s'est produit",
      );
      setAllStudents([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    if (!show) return;
    fetchYears();
  }, [show, selectedYear?.id]);

  // 👉 Change handler pour l'Année Académique
  const handleYearChange = async (selectedId: string) => {
    const selected = years.find((c: any) => c.id.toString() === selectedId);
    if (selected) setSelectedAcademicYear(selected);

    setAllClasses([]);
    setAllStudents([]);
    setSelectedClass(null);
    setSelectedStudent(null);
    setValue("id_class", "");
    setValue("id_studentProfil", "");
    setValue("id_academicYear", selectedId, { shouldValidate: true });

    // ✅ true = auto-sélectionner la première classe ET ses étudiants
    await fetchClasses(selectedId, undefined, undefined, true);
  };

  // 👉 Change handler pour la Classe
  const handleClassChange = async (selectedId: string) => {
    const selected = allClasses.find(
      (c: any) => c.id.toString() === selectedId,
    );
    if (selected) setSelectedClass(selected);

    setAllStudents([]);
    setSelectedStudent(null);
    setValue("id_studentProfil", "");
    setValue("id_class", selectedId, { shouldValidate: true });

    // ✅ Passer un flag pour auto-sélectionner le premier étudiant
    await fetchStudents(selectedId, undefined, true);
  };

  // 👉 Change handler pour l'Étudiant (pas de fetch, juste update state/form)
  const handleStudentChange = (selectedId: string) => {
    const selected = allStudents.find(
      (c: any) => c.StudentProfil.id.toString() === selectedId,
    );
    if (selected) setSelectedStudent(selected);
    setValue("id_studentProfil", selectedId, { shouldValidate: true });
  };

  const fetchMemory = async () => {
    try {
      const response = await getMemoryByID(idMemory);
      if (!response) {
        // Si aucune année trouvé
        throw new Error("Mémoire introuvable");
      }
      // console.log("Response edit", response)
      const finalData = response.data.memory;

      setMemoryData(finalData); // Stocker les données du mémoire

      reset({
        // Si année trouvé on rempli les champs du formulaire
        id_academicYear: finalData.Class.Level.AcademicYear.id,
        id_class: finalData.Class.id,
        id_studentProfil: finalData.StudentProfil.id,
        title: finalData.title,
        director_memory: finalData.director_memory,
        member_of_jury1: finalData.member_of_jury1,
        member_of_jury2: finalData.member_of_jury2,
        defense_date: finalData.defense_date?.slice(0, 10),
        place_of_defense: finalData.place_of_defense,
        note: String(finalData.note ?? ""),
      });

      setSelectedAcademicYear(finalData.Class.Level.AcademicYear);

      // ✅ On passe les IDs directement, sans dépendre du state
      await fetchClasses(
        finalData.Class.Level.AcademicYear.id,
        finalData.Class.id, // preselectedClassId
        finalData.StudentProfil.id, // preselectedStudentId
      );
    } catch (error: any) {
      // Si erreur (Toast Error)
      onClose();
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show || !idMemory) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    fetchMemory();
  }, [show, idMemory]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Modifier un Mémoire</Modal.Title>
        </Modal.Header>
        <Modal.Body id="edit_memoire_modal">
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-12 row-cols-md-12">
              <div className="card shadow-sm border-dark rounded">
                <div className="card-header bg-light d-flex align-items-center justify-content-between p-2">
                  <h5 className="mb-0">Informations de l'étudiant</h5>
                </div>
                <div className="card-body pb-0 pt-2 row">
                  <div className="col-xxl-4 col-xl-4 col-md-4">
                    <div className="mb-2">
                      <label
                        className="form-label mb-1"
                        htmlFor="validationServer01"
                      >
                        Année Universitaire
                      </label>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toString()
                            .toLowerCase()
                            .includes(input.toString().toLowerCase())
                        }
                        size="large"
                        style={{ width: "100%" }}
                        getPopupContainer={getModalContainer}
                        placeholder="Sélectionner une année"
                        value={selectedAcademicYear?.id || undefined}
                        onChange={handleYearChange}
                        status={errors.id_academicYear ? "error" : undefined}
                        options={years.map((year) => ({
                          label: `Année ${year.label}`,
                          value: year.id,
                        }))}
                        className="mb-2"
                      />
                      {errors.id_academicYear && (
                        <div className="invalid-feedback d-block">
                          {errors.id_academicYear.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-xxl-8 col-xl-8 col-md-8">
                    <div className="mb-2">
                      <label
                        className="form-label mb-1"
                        htmlFor="validationServer01"
                      >
                        Classe
                      </label>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toString()
                            .toLowerCase()
                            .includes(input.toString().toLowerCase())
                        }
                        size="large"
                        style={{ width: "100%" }}
                        getPopupContainer={getModalContainer}
                        placeholder="Sélectionner une classe"
                        value={selectedClass?.id || undefined}
                        onChange={handleClassChange}
                        status={errors.id_class ? "error" : undefined}
                        options={allClasses.map((classItem: any) => ({
                          label: `${classItem.level.abbreviation} - ${classItem.label}`,
                          value: classItem.id,
                        }))}
                        className="mb-2"
                      />
                      {errors.id_class && (
                        <div className="invalid-feedback d-block">
                          {errors.id_class.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-xxl-6 col-xl-6 col-md-6">
                    <div className="mb-3">
                      <label
                        className="form-label mb-1"
                        htmlFor="validationServer01"
                      >
                        Étudiant
                      </label>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toString()
                            .toLowerCase()
                            .includes(input.toString().toLowerCase())
                        }
                        size="large"
                        style={{ width: "100%" }}
                        getPopupContainer={getModalContainer}
                        placeholder="Sélectionner un étudiant"
                        value={selectedStudent?.StudentProfil?.id || undefined}
                        onChange={handleStudentChange}
                        status={errors.id_studentProfil ? "error" : undefined}
                        options={allStudents.map((student: any) => ({
                          label: `${student.StudentProfil.lastname} ${student.StudentProfil.firstname}`,
                          value: student.StudentProfil.id,
                        }))}
                        className="mb-2"
                      />
                      {errors.id_studentProfil && (
                        <div className="invalid-feedback d-block">
                          {errors.id_studentProfil.message}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedStudent && (
                    <div className="col-xxl-3 col-xl-3 col-md-3">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          N° Matricule
                        </label>
                        <input
                          type="text"
                          disabled
                          value={selectedStudent.StudentProfil.matricule || ""}
                          className={"form-control"}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card shadow-sm border-dark rounded">
                <div className="card-header bg-light d-flex align-items-center justify-content-between p-2">
                  <h5 className="mb-0">Informations Mémoire</h5>
                </div>
                <div className="card-body pb-0 pt-2 row">
                  <div className="col-xxl-12 col-xl-12 col-md-12">
                    <div className="mb-2">
                      <label
                        className="form-label"
                        htmlFor="validationServer01"
                      >
                        Thème
                      </label>
                      <input
                        {...register("title")}
                        type="text"
                        className={
                          errors.title
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        placeholder="Ex: Analyse de l’impact des NG Firewalls"
                      />
                      {errors.title && (
                        <div className="invalid-feedback">
                          {errors.title.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-xxl-4 col-xl-4 col-md-4">
                    <div className="mb-2">
                      <label
                        className="form-label"
                        htmlFor="validationServer01"
                      >
                        Encadrant
                      </label>
                      <input
                        {...register("director_memory")}
                        type="text"
                        className={
                          errors.director_memory
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        placeholder="Ex: Pr. TRAORE Mohamed"
                      />
                      {errors.director_memory && (
                        <div className="invalid-feedback">
                          {errors.director_memory.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-xxl-4 col-xl-4 col-md-4">
                    <div className="mb-2">
                      <label
                        className="form-label"
                        htmlFor="validationServer01"
                      >
                        Jury 1
                      </label>
                      <input
                        {...register("member_of_jury1")}
                        type="text"
                        className={
                          errors.member_of_jury1
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        placeholder="Ex: Mamadou TOGO"
                      />
                      {errors.member_of_jury1 && (
                        <div className="invalid-feedback">
                          {errors.member_of_jury1.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-xxl-4 col-xl-4 col-md-4">
                    <div className="mb-2">
                      <label
                        className="form-label"
                        htmlFor="validationServer01"
                      >
                        Jury 2
                      </label>
                      <input
                        {...register("member_of_jury2")}
                        type="text"
                        className={
                          errors.member_of_jury2
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        placeholder="Ex: SANOGO Boubacar"
                      />
                      {errors.member_of_jury2 && (
                        <div className="invalid-feedback">
                          {errors.member_of_jury2.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-xxl-4 col-xl-4 col-md-4">
                    <div className="mb-2">
                      <label
                        className="form-label"
                        htmlFor="validationServer01"
                      >
                        Date de soutenance
                      </label>
                      <input
                        {...register("defense_date")}
                        type="date"
                        className={
                          errors.defense_date
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                      />
                      {errors.defense_date && (
                        <div className="invalid-feedback">
                          {errors.defense_date.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-xxl-4 col-xl-4 col-md-4">
                    <div className="mb-2">
                      <label
                        className="form-label"
                        htmlFor="validationServer01"
                      >
                        Lieu de soutenance
                      </label>
                      <input
                        {...register("place_of_defense")}
                        type="text"
                        className={
                          errors.place_of_defense
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        placeholder="Ex: Salle multifonctionnelle"
                      />
                      {errors.place_of_defense && (
                        <div className="invalid-feedback">
                          {errors.place_of_defense.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-xxl-4 col-xl-4 col-md-4">
                    <div className="mb-2">
                      <label
                        className="form-label"
                        htmlFor="validationServer01"
                      >
                        Note de soutenance
                      </label>
                      <input
                        {...register("note")}
                        type="text"
                        className={
                          errors.note
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        placeholder="Ex: 16.5"
                      />
                      {errors.note && (
                        <div className="invalid-feedback">
                          {errors.note.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditMemoryModal,
  [buildPermission(Resource.MEMOIRE, Action.UPDATE)],
  "masquer",
);
